// The NSF membership check on screen: a badge per player on the roster and per pending
// registration, and the dialog behind it, where "Bruk NSF-data" puts NSF's details on a
// player. Shown only to the tournament's arbiters, only for the groups with
// "NSF-medlemskap kreves" switched on.
//
// What a result means is js/utils/nsf-membership.js; asking the server and keeping the
// answer is js/services/nsf-membership.js. Nothing changes on a player unless the
// arbiter says so here: the check itself only reads.

import { state, saveToLocalStorage, getActiveSettings } from '../state.js';
import { escapeHtml } from '../utils/formatting.js';
import {
    isNsfRequired, nsfBadge, nsfChanges, nsfChangesAsFields, nsfResultFromRegistration, nsfResultFromSearch
} from '../utils/nsf-membership.js';
import {
    nsfCheckState, nsfResultForPlayer, nsfResultForRegistration, nsfYearForGroup,
    nsfPickedYear, refreshNsfCheck, notifyNsfChange
} from '../services/nsf-membership.js';

// The group the roster on screen belongs to: null for a tournament without groups.
function activeGroupId() {
    const t = state.activeTournament;
    return t && Array.isArray(t.groups) && t.groups.length > 0 ? (state.activeGroupId || null) : null;
}

function memberName(m) {
    return m ? [m.givenName, m.lastName].filter(Boolean).join(' ') : '';
}

function personName(p) {
    return [p.givenName, p.lastName].filter(Boolean).join(' ').trim() || tr('Unnamed');
}

const ERROR_TEXT = {
    register_unavailable: 'NSF\'s member list has not been imported yet.',
    rate_limited: 'Too many checks this hour. Try again later.',
    not_arbiter: 'Only the tournament\'s arbiters can check memberships.',
    not_admin: 'The NSF check is open to administrators only for now.',
    network: 'No connection to the server.'
};

function errorText(code) {
    return tr(ERROR_TEXT[code] || 'The check failed.');
}

function badgeSpan(badge, vars, kind, id, groupId) {
    const label = tr(badge.label, vars);
    const title = tr(badge.title, vars);
    const group = groupId === null || groupId === undefined ? '' : String(groupId);
    return `<span class="status-badge nsf-badge ${badge.tone}" role="button" tabindex="0"
        data-nsf-kind="${kind}" data-nsf-id="${escapeHtml(id)}" data-nsf-group="${escapeHtml(group)}"
        title="${escapeHtml(title)}">${escapeHtml(label)}</span>`;
}

// The badge for a person whose group requires membership, from their result.
function badgeFor(result, person, groupId, kind, id) {
    const s = nsfCheckState();
    if (s.status === 'off') return '';
    if (!result) {
        // Not in a check yet, but picked from NSF's register in the player search, or
        // found among NSF's paid members when they registered.
        const year = nsfYearForGroup(groupId);
        const known = (kind === 'player' ? nsfResultFromSearch(nsfPickedYear(person), year) : null)
            || nsfResultFromRegistration(person, year);
        if (known) return badgeSpan(nsfBadge(known), { year, last: known.member.membershipYear, name: '' }, kind, id, groupId);
    }
    if (result === undefined) {
        // No answer for this tournament yet: on its way, or it failed.
        const waiting = s.status === 'error'
            ? { tone: 'completed', label: 'NSF', title: errorText(s.error) + ' ' + tr('Click to try again.') }
            : { tone: 'completed', label: 'NSF …', title: 'Checking NSF membership…' };
        return badgeSpan(waiting, {}, kind, id, groupId);
    }
    const member = result && result.member;
    const changes = kind === 'player' && member && result.status === 'unique'
        ? nsfChanges(person, member, getActiveSettings()) : [];
    const vars = {
        year: nsfYearForGroup(groupId),
        last: member ? member.membershipYear : '',
        name: memberName(member)
    };
    return badgeSpan(nsfBadge(result, changes.length), vars, kind, id, groupId);
}

/** The badge for a player on the roster on screen, or '' when there is none to show. */
export function nsfPlayerBadgeHtml(player) {
    const groupId = activeGroupId();
    if (state.isSpectatorMode || !player || !isNsfRequired(state.activeTournament, groupId)) return '';
    return badgeFor(nsfResultForPlayer(player), player, groupId, 'player', player.id);
}

/** The badge for a pending registration, or '' when its group does not require membership. */
export function nsfRegistrationBadgeHtml(reg) {
    if (state.isSpectatorMode || !reg) return '';
    const t = state.activeTournament;
    const groupId = t && Array.isArray(t.groups) && t.groups.length > 0 ? reg.groupId : null;
    if (!isNsfRequired(t, groupId)) return '';
    return badgeFor(nsfResultForRegistration(reg.id), reg, groupId, 'registration', reg.id);
}

/** Make every badge under `root` open its dialog, by mouse or keyboard. */
export function wireNsfBadges(root) {
    if (!root) return;
    root.querySelectorAll('.nsf-badge').forEach(el => {
        const open = () => {
            if (el.dataset.nsfKind === 'registration') openRegistrationDialog(el.dataset.nsfId, el.dataset.nsfGroup || null);
            else openPlayerDialog(el.dataset.nsfId);
        };
        el.addEventListener('click', open);
        el.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
        });
    });
}

// ----- the dialogs -----------------------------------------------------------

const FIELD_LABELS = { nsfId: 'NSF ID', club: 'Club', fideId: 'FIDE ID', birthYear: 'Birth Year', kFactor: 'K-Factor' };

// One line about a member, as much as NSF lets us show: "Ola Nordmann · Dragen · 2012".
function memberLine(m) {
    return [memberName(m), m.club, m.birthYear, m.fideId ? `FIDE ${m.fideId}` : '']
        .filter(Boolean).map(String).join(' · ');
}

function membershipLine(m, year) {
    if (!m || !m.membershipYear) return tr('NSF\'s list gives no membership year.');
    return m.membershipYear >= year
        ? tr('Paid NSF member for {year}.', { year })
        : tr('Paid for {last}, not yet for {year}.', { last: m.membershipYear, year });
}

function changeLine(c) {
    return `${tr(FIELD_LABELS[c.field])}: ${c.from || '–'} → ${c.to}`;
}

// Put the chosen changes on the player, and save the way any edit is saved.
function applyChanges(playerId, changes) {
    const i = state.players.findIndex(p => String(p.id) === String(playerId));
    if (i === -1 || changes.length === 0) return;
    state.players[i] = { ...state.players[i], ...nsfChangesAsFields(changes) };
    saveToLocalStorage();
    notifyNsfChange();
}

// A member is shown, with what using their details would change, and the arbiter decides.
async function offerMember(player, member, year, probable) {
    // The K for the list the roster on screen is rated on (the player's own group).
    const changes = nsfChanges(player, member, getActiveSettings());
    const lines = [memberLine(member), membershipLine(member, year)];
    if (probable) lines.push('', tr('Only part of the name matches. Is this {name}?', { name: personName(player) }));
    if (changes.length === 0) {
        lines.push('', tr('The player already has NSF\'s details.'));
        await window.Dialog.alert(lines.join('\n'), tr('NSF Membership'), 'success');
        return;
    }
    lines.push('', tr('Using NSF\'s details changes:'), ...changes.map(changeLine));
    const options = [{ text: tr('Use NSF\'s details'), value: 'all', variant: 'primary' }];
    const idChange = changes.find(c => c.field === 'nsfId');
    if (idChange && changes.length > 1) options.push({ text: tr('Only the NSF ID'), value: 'id', variant: 'outline' });
    options.push({ text: tr('Cancel'), value: null, variant: 'outline' });
    const choice = await window.Dialog.prompt(lines.join('\n'), tr('NSF Membership'), options, probable ? 'question' : 'info');
    if (choice === 'all') applyChanges(player.id, changes);
    else if (choice === 'id') applyChanges(player.id, [idChange]);
}

async function chooseCandidate(player, result, year) {
    const candidates = Array.isArray(result.candidates) ? result.candidates : [];
    const owner = candidates.find(c => c.tier === 'conflict');
    const message = result.status === 'conflict' && owner
        ? tr('The NSF ID {id} on {name} belongs to {other}. Who is this player?', { id: owner.nsfId, name: personName(player), other: memberName(owner) || owner.nsfId })
        : tr('Several NSF members fit {name}. Which one is it?', { name: personName(player) });
    const options = candidates.map((c, i) => ({ text: memberLine(c) || c.nsfId, value: String(i), variant: 'outline' }));
    options.push({ text: tr('None of them'), value: null, variant: 'outline' });
    const choice = await window.Dialog.prompt(message, tr('NSF Membership'), options, result.status === 'conflict' ? 'warning' : 'question');
    if (choice === null || choice === undefined) return;
    const picked = candidates[Number(choice)];
    if (picked) await offerMember(player, picked, year, false);
}

async function openPlayerDialog(playerId) {
    const player = state.players.find(p => String(p.id) === String(playerId));
    if (!player) return;
    const result = nsfResultForPlayer(player);
    if (!result) {
        // Not checked yet (added since the last save, or the check failed): check now.
        await refreshNsfCheck({ force: true, immediate: true });
        return;
    }
    const year = nsfYearForGroup(activeGroupId());
    if (result.status === 'unique' || result.status === 'probable') {
        await offerMember(player, result.member, year, result.status === 'probable');
    } else if (result.status === 'ambiguous' || result.status === 'conflict') {
        await chooseCandidate(player, result, year);
    } else if (result.fide) {
        await window.Dialog.alert(
            tr('{name} is not among NSF\'s paid members for {year}, but has a FIDE ID, which is enough to play in NGP. Only NSF members get GP points.', { name: personName(player), year }),
            tr('NSF Membership'));
    } else {
        await window.Dialog.alert(
            tr('{name} is not among NSF\'s paid members for {year}. Check how the name and the year of birth are spelled.', { name: personName(player), year }),
            tr('NSF Membership'));
    }
}

// A pending registration is shown, not changed: its details are the registrant's own
// until the arbiter approves it, and then "Bruk NSF-data" works on the player.
async function openRegistrationDialog(registrationId, groupId) {
    const result = nsfResultForRegistration(registrationId);
    if (!result) {
        await refreshNsfCheck({ force: true, immediate: true });
        return;
    }
    const year = nsfYearForGroup(groupId);
    let message;
    if (result.member) {
        message = [memberLine(result.member), membershipLine(result.member, year)];
        if (result.status === 'probable') message.push('', tr('Only part of the name matches.'));
    } else if (result.status === 'ambiguous' || result.status === 'conflict') {
        message = [tr('Several NSF members fit this registration:'), ...(result.candidates || []).map(memberLine)];
    } else if (result.fide) {
        message = [tr('Not among NSF\'s paid members for {year}, but has a FIDE ID: may play in NGP, without GP points.', { year })];
    } else {
        message = [tr('Not among NSF\'s paid members for {year}.', { year })];
    }
    message.push('', tr('Once the registration is approved, NSF\'s details can be used from the player list.'));
    await window.Dialog.alert(message.join('\n'), tr('NSF Membership'));
}
